import { Box, useTheme } from "@mui/material";
import Typography from "../typography";

export const DEFAULT_PROFILE_TAB_HEIGHT = 70;

const TAB_WIDTH = 240;

export interface TabsProps {
  selectedTab: string;
  setSelectedTab: (tab: string) => void;
  tabs: { label: string; path: string }[];
  align?: "left" | "right" | "center";
}

const Tabs: React.FC<TabsProps> = (data: TabsProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        gap: theme.spacing(1),
        // Five tabs at their full width need 1264px, so below md they shrink to
        // their labels and the row scrolls rather than overflowing the page.
        justifyContent: { xs: "flex-start", md: data.align ?? "center" },
        padding: `0 ${theme.spacing(2)}`,
        overflowX: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {data.tabs.map((tab) => {
        return (
          <Tab
            key={tab.path}
            label={tab.label}
            isSelected={data.selectedTab === tab.path}
            onClick={() => {
              data.setSelectedTab(tab.path);
            }}
          />
        );
      })}
    </Box>
  );
};

const Tab: React.FC<{
  label: string;
  isSelected: boolean;
  onClick: () => void;
}> = ({ label, onClick, isSelected }) => {
  const theme = useTheme();

  return (
    <Box
      onClick={onClick}
      sx={{
        // Five tabs at a flat 240px need 1264px, which a 13" laptop does not
        // have — so above md they share the row and only cap at the old width.
        width: { xs: "auto", md: TAB_WIDTH },
        maxWidth: { md: TAB_WIDTH },
        minWidth: { xs: 96, md: 120 },
        flex: { xs: "0 0 auto", md: "1 1 0" },
        paddingX: { xs: 1.5, md: 0 },
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        cursor: "pointer",
        height: DEFAULT_PROFILE_TAB_HEIGHT,
        "&:hover": { backgroundColor: theme.palette.action.hover },
      }}
    >
      <Typography
        variant="h3"
        style={{ fontWeight: "bold", userSelect: "none" }}
        sx={{ fontSize: { xs: "1rem", md: "1.4rem" } }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          width: "100%",
          height: "10px",
          ...(isSelected
            ? { backgroundColor: theme.palette.warning.main }
            : {}),
        }}
      />
    </Box>
  );
};

export default Tabs;
